import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  IFacilitySignupPayload,
  IFacilityList,
  IUpdateState,
  IFacilityTypeResponse,
  IFetchFacilityTypesParams,
  IFetchFacilityTypeDocumentRequirementsParams,
  IFacilityTypeDocumentRequirement,
  IFacilitySignupResponse,
  IFacilityDocumentUploadResponse,
  IFacilityUser,
  IUpdateFacilityProfilePayload,
  IUpdateFacilityProfileResponse,
  IFacilityProfile,
  IFacilityShiftHistoryList,
  IFacilityDashboardStatistics,
  IFacilityShiftStatusChart,
  IFacilityShiftCalendar,
  IFacilityAssignedWorker,
  IAcceptRejectFacilityDocumentResponse
} from './types';
import apiCall from 'utils/api';

export const fetchFacilityList = createAsyncThunk(
  'facility/fetchFacilityList',
  async ({ page, pageSize, search, locationName, orderBy, order, status }: { page: number; pageSize: number; search: string; locationName?: string; orderBy?: string; order?: 'asc' | 'desc'; status?: string }, { rejectWithValue }) => {
    try {
      let url = `/api/facility/list/?page=${page}&page_size=${pageSize}&search=${encodeURIComponent(search)}`;
      if (locationName) {
        url += `&location_name=${encodeURIComponent(locationName)}`;
      }
      if (orderBy) {
        url += `&order_by=${orderBy}`;
      }
      if (order) {
        url += `&order=${order}`;
      }
      if (status && status !== 'All') {
        url += `&status=${encodeURIComponent(status)}`;
      }
      const response = await apiCall<{ data: IFacilityList }>('get', url);
      return response.data.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility list');
    }
  }
);

export const fetchFacilityStatistics = createAsyncThunk(
  'facility/fetchFacilityStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: any }>('get', '/api/facility/statistics/');
      return response.data.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility statistics');
    }
  }
);

export const fetchFacilityLocations = createAsyncThunk(
  'facility/fetchFacilityLocations',
  async ({ page, pageSize, search }: { page: number; pageSize: number; search: string }, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: any }>('get', `/api/facility/locations/?page=${page}&page_size=${pageSize}&search=${encodeURIComponent(search)}`);
      return response.data.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility locations');
    }
  }
);

export const fetchFacilityDetails = createAsyncThunk(
  'facility/fetchFacilityDetails',
  async (facilityId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: any }>('get', `/api/facility/details/${facilityId}/`);
      return response.data.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility details');
    }
  }
);

export const updateFacilityStatus = createAsyncThunk(
  'facility/updateFacilityStatus',
  async ({ facilityId, action, reason }: { facilityId: number; action: string; reason?: string }, { rejectWithValue }) => {
    try {
      const payload: any = { action };
      if (reason) payload.reason = reason;
      const response = await apiCall<{ data: any }>('put', `/api/facility/status/${facilityId}/`, payload);
      return response.data.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to update facility status');
    }
  }
);

export const updateFacilityDocumentDates = createAsyncThunk(
  'facility/updateFacilityDocumentDates',
  async ({ documentId, document_issued_date, document_expiring_date }: {
    documentId: number;
    document_issued_date?: string;
    document_expiring_date?: string
  }, { rejectWithValue }) => {
    try {
      const payload: { document_issued_date?: string; document_expiring_date?: string } = {};
      if (document_issued_date !== undefined) {
        payload.document_issued_date = document_issued_date;
      }
      if (document_expiring_date !== undefined) {
        payload.document_expiring_date = document_expiring_date;
      }

      const response = await apiCall<{ data: { status: boolean, message: string }; status: boolean }>('patch', `/api/facility/documents/${documentId}/update-dates/`, payload);

      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to update facility document dates');
    }
  }
);

export const markFacilityAsViewed = createAsyncThunk(
  'facility/markFacilityAsViewed',
  async (facilityId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IUpdateState; status: boolean }>('patch', `/api/facility/viewed/${facilityId}/`);
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to mark facility as viewed');
    }
  }
);

export const fetchFacilityShiftStatics = createAsyncThunk(
  'facility/fetchFacilityShiftStatics',
  async (facilityId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: any }>('get', `/api/shifts/statistics/${facilityId}/`);
      return response.data.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility shift statistics');
    }
  }
);

export const acceptRejectFacilityDocument = createAsyncThunk(
  'facility/acceptRejectFacilityDocument',
  async ({ documentId, action, remarks, document_issued_date, document_expiring_date }: {
    documentId: number;
    action: 'accept' | 'reject';
    remarks: string;
    document_issued_date?: string;
    document_expiring_date?: string;
  }, { rejectWithValue }) => {
    try {
      const payload: any = { action, remarks };
      if (document_issued_date) payload.document_issued_date = document_issued_date;
      if (document_expiring_date) payload.document_expiring_date = document_expiring_date;

      const response = await apiCall<IAcceptRejectFacilityDocumentResponse>('patch', `/api/facility/documents/${documentId}/accept-reject/`, payload);
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to accept or reject facility document');
    }
  }
);

export const facilitySignup = createAsyncThunk<IFacilitySignupResponse, IFacilitySignupPayload>(
  'facility/facilitySignup',
  async (facilityData: IFacilitySignupPayload, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ status: boolean, data: IFacilitySignupResponse }>('post', '/api/facility/create-with-token/', facilityData);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create facility');
    }
  }
);

export const fetchFacilityTypes = createAsyncThunk(
  'facility/fetchFacilityTypes',
  async ({ page, page_size, search }: IFetchFacilityTypesParams, { rejectWithValue }) => {
    try {
      let url = `/auth/facility-type/?page=${page || 1}&page_size=${page_size || 5}`;
      if (search !== undefined && search !== null) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      const response = await apiCall<{ data: IFacilityTypeResponse }>('get', url);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility types');
    }
  }
);

export const uploadFacilityDocument = createAsyncThunk<
  IFacilityDocumentUploadResponse,
  { document: File, document_name: string, document_type: number }
>(
  'facility/uploadFacilityDocument',
  async ({ document, document_name, document_type }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('document', document);
      formData.append('document_name', document_name);
      formData.append('document_type', document_type.toString());

      const response = await apiCall<IFacilityDocumentUploadResponse>('post', '/api/facility/upload-documents/', formData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to upload document');
    }
  }
);

export const fetchFacilityShiftHistory = createAsyncThunk(
  'facility/fetchFacilityShiftHistory',
  async ({ page, pageSize, search, status, startDate, endDate }: { page: number; pageSize: number; search?: string; status?: string; startDate?: string; endDate?: string }, { rejectWithValue }) => {
    try {
      let url = `/api/shifts/facility/history/?page=${page}&page_size=${pageSize}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (status) url += `&status=${encodeURIComponent(status)}`;
      if (startDate) url += `&start_date=${startDate}`;
      if (endDate) url += `&end_date=${endDate}`;

      const response = await apiCall<{ data: IFacilityShiftHistoryList }>('get', url);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility shift history');
    }
  }
);

export const fetchFacilityTypeDocumentRequirements = createAsyncThunk(
  'facility/fetchFacilityTypeDocumentRequirements',
  async ({ facility_type_id }: IFetchFacilityTypeDocumentRequirementsParams, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IFacilityTypeDocumentRequirement[] }>('get', `/api/organization/facility-type-document-requirements/?facility_type_id=${facility_type_id}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility type document requirements');
    }
  }
);

export const updateFacilityViolationFlag = createAsyncThunk(
  'facility/updateFacilityViolationFlag',
  async ({ facilityId, violation_flag }: { facilityId: number; violation_flag: boolean }, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: any }>('put', `/api/facility/flag/${facilityId}/`, { violation_flag });
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to update facility violation flag');
    }
  }
);

export const fetchFacilityUsers = createAsyncThunk(
  'facility/fetchFacilityUsers',
  async (facilityId: number | undefined, { rejectWithValue }) => {
    try {
      let url = '/api/facility/facility-user/';
      if (facilityId) {
        url += `?facility=${facilityId}`;
      }
      const response = await apiCall<{ status: boolean, data: IFacilityUser[] }>('get', url);
      return response.data.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any

      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility users');
    }
  }
);

export const updateFacilityProfile = createAsyncThunk(
  'facility/updateFacilityProfile',
  async (data: IUpdateFacilityProfilePayload, { rejectWithValue }) => {
    try {
      const response = await apiCall<IUpdateFacilityProfileResponse>('put', '/api/facility/profile/', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update facility profile');
    }
  }
);

export const fetchFacilityProfile = createAsyncThunk(
  'facility/fetchFacilityProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IFacilityProfile }>('get', '/api/facility/profile/');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility profile');
    }
  }
);

export const uploadFacilityDocument2 = createAsyncThunk(
  'facility/uploadFacilityDocument2',
  async ({ document, document_name, document_type }: { document: File, document_name: string, document_type: string }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('document', document);
      formData.append('document_name', document_name);
      formData.append('document_type', document_type);

      const response = await apiCall<{ status: boolean, message: string }>('post', '/api/facility/upload-documents/', formData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to upload document');
    }
  }
);

export const fetchFacilityReportStats = createAsyncThunk(
  'facility/fetchFacilityReportStats',
  async ({ start_date, end_date }: { start_date?: string, end_date?: string }, { rejectWithValue }) => {
    try {
      let url = '/api/shifts/facility/report/stats/';
      const params = new URLSearchParams();
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const response = await apiCall<{ data: any }>('get', url);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility report stats');
    }
  }
);

export const fetchFacilityWorkerAttendance = createAsyncThunk(
  'facility/fetchFacilityWorkerAttendance',
  async ({ page, pageSize, search, orderBy, order }: { page: number; pageSize: number; search?: string; orderBy?: string; order?: 'asc' | 'desc' }, { rejectWithValue }) => {
    try {
      let url = `/api/shifts/facility/worker-attendance/?page=${page}&page_size=${pageSize}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      if (orderBy) {
        url += `&order_by=${orderBy}`;
      }
      if (order) {
        url += `&order=${order}`;
      }
      const response = await apiCall<{ data: any }>('get', url);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility worker attendance');
    }
  }
);

export const fetchFacilityDashboardStatistics = createAsyncThunk(
  'facility/fetchFacilityDashboardStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IFacilityDashboardStatistics }>('get', '/api/facility/dashboard/statistics/');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility dashboard statistics');
    }
  }
);

export const fetchFacilityShiftStatusChart = createAsyncThunk(
  'facility/fetchFacilityShiftStatusChart',
  async ({ startDate, endDate }: { startDate?: string; endDate?: string }, { rejectWithValue }) => {
    try {
      let url = '/api/facility/dashboard/chart/shift-status/';
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const response = await apiCall<{ data: IFacilityShiftStatusChart }>('get', url);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility shift status chart');
    }
  }
);

export const fetchFacilityShiftCalendar = createAsyncThunk(
  'facility/fetchFacilityShiftCalendar',
  async ({ year, month }: { year: number | string; month: number | string }, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IFacilityShiftCalendar }>('get', `/api/shifts/calendar/?year=${year}&month=${month}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility shift calendar');
    }
  }
);

export const fetchFacilityAssignedWorkers = createAsyncThunk(
  'facility/fetchFacilityAssignedWorkers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IFacilityAssignedWorker[] } | { message: string }>('get', '/api/facility/dashboard/assigned-workers/');

      const payload = response.data;

      // Check if payload has data array
      if ('data' in payload && Array.isArray(payload.data)) {
        return payload.data;
      }

      // If message is present (e.g. "no data found"), return empty array
      return [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility assigned workers');
    }
  }
);
